import React, { useState, FormEvent, useEffect, useMemo } from 'react'
import { connect, useDispatch } from 'react-redux'
import { Stack, Paper, Container, TextField } from '@mui/material'
import ProgressButton from '../../Utils/ProgressButton'
import { entityTextField, entitySelectField } from '../../Utils/EntityField'
import { AppState } from '../../../reducers/index'
import { upsertMassNotification } from '../../../actions/massNotification/massNotificationUpsert'
import { MassNotificationDetailState } from '../../../reducers/massNotification/massNotificationDetail'
import MassNotification from '../../../models/MassNotification'
import { useTranslation } from 'react-i18next'
import { ISTableTitle } from '../../Utils/ISTable'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { fetchMassNotification } from '../../../actions/massNotification/massNotificationDetail'
import { BackButton } from '../../Utils/BackButton'
import { DesktopDateTimePicker, LocalizationProvider } from '@mui/lab'
import AdapterMoment from '@mui/lab/AdapterMoment'
import moment, { Moment } from 'moment'
import { useThemeContext } from '../../../theme/ThemeContext'
import { personTypeToDescription, visiblePersonTypes } from '../../Client/Profile'
import { translateStatusMassNotificationDescription } from './MassNotificationList'

interface Props {
  massNotification: MassNotificationDetailState
}

const empty: MassNotification = {
  id: null,
  title: '',
  message: '',
  url: '',
  scheduledDate: '',
  notificationType: 'M',
  personType: '',
  toPersonTypeId: null,
  status: 'PENDING'
}

const MassNotificationUpsert: React.FC<Props> = ({ massNotification }) => {
  const [changed, setChanged] = useState<boolean>(false)
  const [massNotificationInfo, setMassNotificationInfo] = useState<MassNotification>(massNotification.massNotification || {
    ...empty
  })
  const { t } = useTranslation(['massNotification', 'person'])
  const { theme } = useThemeContext()
  const dispatch = useDispatch()
  const params = useParams<{ massNotificationId: string }>()
  const history = useHistory()
  const location = useLocation()

  useEffect(() => {
    if (params.massNotificationId) {
      dispatch(fetchMassNotification(params.massNotificationId))
    }
  }, [dispatch, params.massNotificationId])


  const change = (newMassNotificationInfo: MassNotification) => {
    setMassNotificationInfo(newMassNotificationInfo)
    setChanged(true)
  }

  const changeMassNotification = async (event: FormEvent) => {
    event.preventDefault()
    try {
      setChanged(false)
      await dispatch(upsertMassNotification(massNotificationInfo))
      setChanged(false)
      if (location.state)
        (location.state as any).update = !(location.state as any).update
      history.goBack()
    }
    catch {
    }
  }

  const updateDate = (newDate: Moment | null) => {
    if (newDate) {
      const scheduledDate = newDate.tz("America/Argentina/Buenos_Aires").format()
      setMassNotificationInfo({ ...massNotificationInfo, scheduledDate })
      setChanged(true)
    }
    else setMassNotificationInfo({ ...massNotificationInfo, scheduledDate: ''})
  }

  useEffect(() => {
    if (massNotification.massNotification) {
      setMassNotificationInfo(params.massNotificationId ? massNotification.massNotification :
        { ...empty }
      )
    }
  }, [massNotification.massNotification, params.massNotificationId])


  const MassNotificationInfoField = useMemo(() => entityTextField(change), [])

  const MassNotificationInfoSelectField = useMemo(() => entitySelectField(change), [])

  const translatePersonTypeDescription = (value: string): string => t(personTypeToDescription(value), { ns: 'person' })

    return (
    <Container>
      <Paper style={{ borderRadius: "10px", margin: "10px" }}>
        <ISTableTitle title={t('notifications')} />
        <Paper style={{ padding: "10px" }}>
          <form onSubmit={changeMassNotification}>
            <MassNotificationInfoField
              key={"title"}
              variant="outlined"
              label={t("title")}
              entity={massNotificationInfo}
              attr='title'
              size="small"
              required
            />
            <MassNotificationInfoField
              key={"message"}
              variant="outlined"
              label={t("message")}
              entity={massNotificationInfo}
              attr='message'
              multiline
              rows={4}
              size="small"
              required
            />
            <MassNotificationInfoField
              key={'url'}
              variant="outlined"
              label={t("url")}
              entity={massNotificationInfo}
              attr={'url'}
              type='url'
              size="small"
            />
            <Stack display="flex" flexDirection="row" flexGrow={1} >
              <MassNotificationInfoSelectField
                key="personType"
                label={t("personType")}
                entity={massNotificationInfo}
                attr="personType"
                menuItems={visiblePersonTypes}
                size='small'
                textAdapter={translatePersonTypeDescription}
                style={{ width: "350px", marginRight: "10px" }}
                required
              />
              <MassNotificationInfoField
                key="toPersonTypeId"
                variant="outlined"
                label={t("toPersonTypeId")}
                entity={massNotificationInfo}
                attr="toPersonTypeId"
                size='small'
                style={{ marginRight: "10px" }}
              />
              <MassNotificationInfoSelectField
                key="status"
                label={t("status")}
                entity={massNotificationInfo}
                attr="status"
                menuItems={['PENDING','CANCELLED']} 
                textAdapter={translateStatusMassNotificationDescription}
                size='small'
                style={{ width: "200px", marginRight: "10px" }}
                required
                disabled={massNotification.massNotification?.id === null}
              />
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DesktopDateTimePicker
                  label={t("scheduledDate")}
                  inputFormat="DD/MM/yyyy HH:mm"
                  ampm={false}
                  minDate={moment()}
                  value={massNotificationInfo.scheduledDate ? moment(new Date(massNotificationInfo.scheduledDate)) : null}
                  onChange={updateDate}
                  renderInput={(params) => (
                    <TextField
                      {...{
                        ...params,
                      }}
                      required
                      sx={{ marginTop: "15px", minWidth: 250, svg: { fill: theme.typography.button.color } }}
                      size="small"
                    />
                  )}
              />
              </LocalizationProvider>
            </Stack>
            <Stack display="flex" flexDirection='row' justifyContent="space-between">
              <BackButton />
              <ProgressButton state={massNotification} disabled={!changed}>
                {t('save')}
              </ProgressButton>
            </Stack>
          </form>
        </Paper>
      </Paper>
    </Container >

  )
}

const mapStateToProps = (state: AppState) => ({
  massNotification: state.massNotificationDetail,
})

export default connect<Props, {}, {}, AppState>(mapStateToProps, {})(MassNotificationUpsert)
